/**
 * Over/Under Pick Adapter
 *
 * Transforms admin preview data into OverUnderPick component props.
 *
 * This adapter serves all three over/under variants (see adapterRegistry):
 *   - total              → match-level main market (no player/team subject)
 *   - team_over_under    → a team is the subject (name = teamName)
 *   - player_over_under  → a player is the subject (name + position + team badge)
 *   - over_under         → legacy alias; branches on config the same way
 *
 * It reads the REAL configuration written by the admin question form
 * (question_metadata.grading.config — see OverUnderPlayerTeamSelection +
 * OverUnderConfigSection) and mirrors the frontend's canonical extraction
 * (apps/frontend/src/utils/pickem-helpers.ts: extractPlayerData /
 * extractStatData / extractPlayerTeamLogo) rather than hardcoding mock values.
 *
 * Match data (kickoff time, home/away teams + logos) is NOT threaded into the
 * preview pipeline, so those stay blank here by design — never a placeholder
 * string like "Preview Mode".
 */

import type { OverUnderPickProps } from '@low6-pickem/ui/components/OverUnderPick';
import { getStatTypeDisplayName } from '@low6-pickem/ui/utils/stat-type-display';
import { formatPlayerNameAsInitialSurname } from '@low6-pickem/ui/utils/name-format';
import { normalizeTeamLogoUrl } from '../../normalizeTeamLogoUrl';
import type { PreviewQuestionData } from '../types';

/**
 * Transforms preview data into OverUnderPick props.
 *
 * @param data - Preview question data from the admin form
 * @returns OverUnderPick component props
 */
export function overUnderPickAdapter(data: PreviewQuestionData): OverUnderPickProps {
  const config = data.questionMetadata?.grading?.config;
  const type = (data.questionType || '').toLowerCase();

  // Match-level main market (DKPLAT-500 Total): no player/team subject, so the
  // component shows the matchup panel instead of a jersey. Mirrors the frontend
  // registry, which keys isMatchLevel off question_type === 'total'.
  const isMatchLevel = type === 'total';

  // Subject (player or team) — mirrors extractPlayerData: prefer a player, then
  // a team, else blank. The subject image is the question-level image
  // (question_image_en) — a jersey for player O/U, a team logo for team O/U.
  let playerName = '';
  let playerPosition = '';
  // playerTeamLogoUrl: the small team badge shown over the player jersey.
  //   - player O/U → the configured team logo (mirrors extractPlayerTeamLogo)
  //   - team O/U   → blank; the main subject image already IS the team logo
  //   - match-level → undefined (no subject panel at all)
  let playerTeamLogoUrl: string | undefined = isMatchLevel ? undefined : '';

  if (!isMatchLevel) {
    // The frontend registry runs the subject through
    // formatPlayerNameAsInitialSurname regardless of whether it's a player or
    // a team ("Patrick Mahomes" -> "P. Mahomes", "Home Team" -> "H. Team"), so
    // the preview does the same.
    if (config?.playerName) {
      // Player over/under
      playerName = formatPlayerNameAsInitialSurname(config.playerName);
      playerPosition = config.playerPosition || '';
      playerTeamLogoUrl = config.teamLogoUrl ? normalizeTeamLogoUrl(config.teamLogoUrl) : '';
    } else if (config?.teamName) {
      // Team over/under — teams have no position, and no separate badge.
      playerName = formatPlayerNameAsInitialSurname(config.teamName);
      playerPosition = '';
      playerTeamLogoUrl = '';
    }
  }

  // Stat — mirrors extractStatData: threshold is the numeric value, the stat
  // code is resolved to a friendly display name (never the raw code).
  const statType = config?.statType || '';
  const statValue = config?.threshold ?? 0;
  const statDisplay = getStatTypeDisplayName(statType);

  // Daily Double is a per-question metadata flag (any type).
  const dailyDouble = Boolean(data.questionMetadata?.dailyDouble);

  return {
    questionId: 'preview',
    questionNumber: undefined,
    points: data.totalPoints,
    earnedPoints: undefined,

    isMatchLevel,

    // Subject (blank for match-level totals)
    playerName,
    playerPosition,
    playerJerseyUrl: data.questionImage || '',
    playerTeamLogoUrl,

    // Stat
    statValue,
    statType: statDisplay,

    // Match data is not available in the preview pipeline — leave blank rather
    // than emitting placeholder text. (Decision: never show "Preview Mode".)
    homeTeamName: undefined,
    awayTeamName: undefined,
    homeTeamLogoUrl: undefined,
    awayTeamLogoUrl: undefined,
    matchTime: '',

    // Popularity + selection: preview is view-only.
    overPercentage: undefined,
    underPercentage: undefined,
    selectedAnswer: undefined,
    onAnswerSelect: () => {},

    // Natural-language copy — same as the live registry adapter.
    questionText: data.questionText,
    subtext: data.questionSubtext,

    isCorrect: undefined,
    disabled: true,
    questionMetadata: dailyDouble ? { dailyDouble: true } : undefined,
  };
}
